/**
 * features/reports/BulkSend.tsx
 * ------------------------------------------------------------------
 * FEATURE #6 — per-class bulk delivery of generated report PDFs.
 * Admin only. Email via sendReportEmail, WhatsApp via
 * sendWhatsAppReport (Meta Cloud API with wa.me fallback).
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Mail,
  MessageCircle,
  CheckSquare,
  Square,
  Loader2,
  ExternalLink,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { db, functions } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { Card, PageHeader, Spinner, Button } from '@/components/ui/primitives';
import { TermKey, termLabel } from '@/lib/school.config';
import type { GeneratedReport } from '@/types';
import { useGeneratedBatches } from '@/features/reports/useGeneratedBatches';
import GeneratedBatchesList from '@/features/reports/GeneratedBatchesList';

type SendResult = 'sent' | 'link' | string;

export default function BulkSend({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { classes, students, loading } = useData();
  const { batches, loading: batchesLoading } = useGeneratedBatches(year, term);
  const generatedIds = useMemo(() => new Set(batches.map((b) => b.classId)), [batches]);
  const [classId, setClassId] = useState('');
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fetching, setFetching] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);
  const [emailResults, setEmailResults] = useState<Record<string, string>>({});
  const [waResults, setWaResults] = useState<Record<string, SendResult>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [waConfigured, setWaConfigured] = useState<boolean | null>(null);

  // Check WhatsApp API status on mount
  useEffect(() => {
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getWhatsAppStatus');
        const res = await fn({}) as { data: { configured: boolean } };
        setWaConfigured(res.data.configured);
      } catch {
        setWaConfigured(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!classId) {
      setReports([]);
      setSelected(new Set());
      return;
    }
    let cancelled = false;
    setFetching(true);
    getDocs(
      query(
        collection(db, COLLECTIONS.generatedReports),
        where('year', '==', year),
        where('term', '==', term),
        where('classId', '==', classId),
      ),
    )
      .then((snap) => {
        if (cancelled) return;
        const rs = snap.docs.map((d) => d.data() as GeneratedReport);
        rs.sort((a, b) => a.studentName.localeCompare(b.studentName));
        setReports(rs);
        const pre = new Set<string>();
        rs.forEach((r) => {
          const stu = students.find((s) => s.id === r.studentId);
          if (stu?.parentContact?.email && stu?.parentContact?.consentToSend) {
            pre.add(r.id);
          }
        });
        setSelected(pre);
      })
      .finally(() => !cancelled && setFetching(false));
    return () => { cancelled = true; };
  }, [classId, year, term, students]);

  const cls = classes.find((c) => c.id === classId);

  const rows = useMemo(
    () =>
      reports.map((r) => {
        const stu = students.find((s) => s.id === r.studentId);
        return {
          report: r,
          email: stu?.parentContact?.email ?? '',
          whatsapp: stu?.parentContact?.whatsapp ?? '',
          consent: stu?.parentContact?.consentToSend ?? false,
        };
      }),
    [reports, students],
  );

  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleAll() {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.report.id)));
  }

  /* ---- SEND EMAILS ---------------------------------------------- */

  async function sendEmails() {
    const targets = rows.filter((r) => selected.has(r.report.id) && r.email);
    if (targets.length === 0) {
      setMsg({ ok: false, text: 'No selected students have a parent email.' });
      return;
    }
    setSending(true);
    setEmailResults({});
    const fn = httpsCallable(functions, 'sendReportEmail');
    let ok = 0;
    let fail = 0;
    let withheld = 0;
    for (const t of targets) {
      try {
        const res = (await fn({
          reportId: t.report.id,
          toEmail: t.email,
          studentName: t.report.studentName,
          className: t.report.className,
          term: termLabel(term),
          year,
        })) as { data: { ok: boolean; reason?: string; withheld?: boolean } };
        if (res.data.ok) { ok++; setEmailResults((r) => ({ ...r, [t.report.id]: 'sent' })); }
        else if (res.data.withheld) { withheld++; setEmailResults((r) => ({ ...r, [t.report.id]: 'withheld' })); }
        else { fail++; setEmailResults((r) => ({ ...r, [t.report.id]: res.data.reason || 'failed' })); }
      } catch (e) {
        fail++;
        setEmailResults((r) => ({ ...r, [t.report.id]: (e as Error).message }));
      }
    }
    setSending(false);
    setMsg({
      ok: fail === 0,
      text: `Email: ${ok} sent${withheld ? `, ${withheld} withheld (fees)` : ''}, ${fail} failed.`,
    });
  }

  /* ---- SEND WHATSAPP -------------------------------------------- */

  async function sendWhatsApp() {
    const targets = rows.filter((r) => selected.has(r.report.id) && r.whatsapp);
    if (targets.length === 0) {
      setMsg({ ok: false, text: 'No selected students have a WhatsApp number.' });
      return;
    }
    setSendingWa(true);
    setWaResults({});
    const fn = httpsCallable(functions, 'sendWhatsAppReport');
    let ok = 0;
    let fail = 0;
    let links = 0;
    for (const t of targets) {
      try {
        const res = (await fn({
          reportId: t.report.id,
          toPhone: t.whatsapp,
          studentName: t.report.studentName,
          className: t.report.className,
          term: termLabel(term),
          year,
          downloadUrl: t.report.downloadUrl || '',
        })) as { data: { ok: boolean; mode?: string; waLink?: string; error?: string } };
        if (res.data.ok) {
          if (res.data.mode === 'link') {
            links++;
            setWaResults((r) => ({ ...r, [t.report.id]: 'link' }));
            // Auto-open the wa.me link for manual sending
            if (res.data.waLink) window.open(res.data.waLink, '_blank');
          } else {
            ok++;
            setWaResults((r) => ({ ...r, [t.report.id]: 'sent' }));
          }
        } else {
          fail++;
          setWaResults((r) => ({ ...r, [t.report.id]: res.data.error || 'failed' }));
        }
      } catch (e) {
        fail++;
        setWaResults((r) => ({ ...r, [t.report.id]: (e as Error).message }));
      }
    }
    setSendingWa(false);
    const parts = [];
    if (ok > 0) parts.push(`${ok} sent via API`);
    if (links > 0) parts.push(`${links} opened as links`);
    if (fail > 0) parts.push(`${fail} failed`);
    setMsg({ ok: fail === 0, text: `WhatsApp: ${parts.join(', ')}.` });
  }

  if (loading) return <Spinner label="Loading…" />;

  return (
    <div>
      <PageHeader
        title="Send Reports"
        subtitle={`${termLabel(term)} ${year} · deliver reports via email and WhatsApp`}
      />

      {msg && (
        <div className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
          msg.ok
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
            : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
        }`}>
          {msg.text}
        </div>
      )}

      <Card className="p-5 mb-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
          Select class
        </label>
        <select
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          className="w-full sm:w-64 rounded-xl border-2 border-gray-200 dark:border-gray-700 px-3 py-2.5 dark:bg-gray-800 dark:text-white"
        >
          <option value="">— choose a class —</option>
          {classes.filter((c) => !c.archived).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{generatedIds.has(c.id) ? '  ✓ generated' : ''}
            </option>
          ))}
        </select>
      </Card>

      {/* Generated classes available to send — click a row to load it. */}
      <div className="mb-4">
        <GeneratedBatchesList
          batches={batches}
          loading={batchesLoading}
          title="Generated — available to send"
          emptyText="No classes have generated reports for this term yet."
          highlightId={classId ? `${year}-${term}-${classId}` : undefined}
          onRowClick={(b) => setClassId(b.classId)}
        />
      </div>

      {fetching && <Spinner label="Loading reports…" />}

      {!fetching && rows.length > 0 && (
        <Card className="overflow-hidden">
          {/* Action bar */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button onClick={toggleAll} className="flex items-center gap-1 text-sm text-primary">
                {selected.size === rows.length
                  ? <><CheckSquare size={16} /> Deselect all</>
                  : <><Square size={16} /> Select all ({rows.length})</>}
              </button>
              <span className="text-xs text-gray-400">
                {selected.size} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                icon={sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                onClick={sendEmails}
                disabled={sending || sendingWa || selected.size === 0}
              >
                {sending ? 'Sending…' : 'Send Emails'}
              </Button>
              <Button
                variant="secondary"
                icon={sendingWa ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                onClick={sendWhatsApp}
                disabled={sending || sendingWa || selected.size === 0}
              >
                {sendingWa ? 'Sending…' : waConfigured ? 'Send WhatsApp' : 'WhatsApp Links'}
              </Button>
            </div>
          </div>

          {/* WhatsApp status indicator */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 text-xs flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${waConfigured ? 'bg-emerald-500' : 'bg-amber-400'}`} />
            {waConfigured
              ? 'WhatsApp Business API connected — messages sent automatically'
              : 'WhatsApp API not configured — opens pre-filled links for manual sending'}
          </div>

          {/* Student rows */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((r) => {
              const on = selected.has(r.report.id);
              const emailSt = emailResults[r.report.id];
              const waSt = waResults[r.report.id];
              return (
                <div
                  key={r.report.id}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 ${
                    on ? '' : 'opacity-60'
                  }`}
                >
                  <button onClick={() => toggle(r.report.id)} className="text-primary shrink-0">
                    {on ? <CheckSquare size={18} /> : <Square size={18} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-gray-800 dark:text-white truncate">
                      {r.report.studentName}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 text-xs text-gray-400 mt-0.5">
                      {r.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={11} className="text-emerald-500" />
                          {r.email}
                        </span>
                      )}
                      {r.whatsapp && (
                        <span className="flex items-center gap-1">
                          <MessageCircle size={11} className="text-emerald-500" />
                          {r.whatsapp}
                        </span>
                      )}
                      {!r.email && !r.whatsapp && (
                        <span className="text-gray-300">No contact info</span>
                      )}
                    </div>
                  </div>

                  {/* Status badges */}
                  <div className="flex items-center gap-2 shrink-0">
                    {emailSt === 'sent' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        <CheckCircle2 size={12} /> Email
                      </span>
                    )}
                    {emailSt === 'withheld' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title="Withheld — outstanding fees (academic hold)">
                        <XCircle size={12} /> Fees hold
                      </span>
                    )}
                    {emailSt && emailSt !== 'sent' && emailSt !== 'withheld' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300" title={emailSt}>
                        <XCircle size={12} /> Email
                      </span>
                    )}
                    {waSt === 'sent' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        <CheckCircle2 size={12} /> WA
                      </span>
                    )}
                    {waSt === 'link' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                        <ExternalLink size={12} /> WA Link
                      </span>
                    )}
                    {waSt && waSt !== 'sent' && waSt !== 'link' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300" title={waSt}>
                        <XCircle size={12} /> WA
                      </span>
                    )}
                  </div>

                  {/* Manual actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {r.report.downloadUrl && (
                      <a
                        href={r.report.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                        title="View PDF"
                      >
                        <ExternalLink size={15} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500">
            Email uses the server function (SMTP must be configured). WhatsApp uses the Meta Business Cloud API
            when configured, or opens pre-filled wa.me links as fallback.
          </div>
        </Card>
      )}

      {!fetching && classId && rows.length === 0 && (
        <Card className="p-8 text-center text-sm text-gray-500">
          No generated reports found for {cls?.name ?? classId} in {termLabel(term)} {year}.
          Generate reports first.
        </Card>
      )}
    </div>
  );
}
